/**
 * PublicRecipesPage - Public recipe database with comments
 * 
 * Features:
 * - Browse public recipes from all users
 * - Filter by category, skin type, goal
 * - Comment on recipes
 * - Copy recipe to own collection
 */

import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  where,
  limit
} from 'firebase/firestore';
import { db, functions } from '../utils/firebase-config';
import { httpsCallable } from 'firebase/functions';
import { normalizeRecipe } from '../utils/recipeSchema';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import SignInPrompt from '../Components/SignInPrompt';
import SafetyNotice from '../Components/SafetyNotice';
import './labcss/publicrecipes.css';
import './labcss/recipe-pages.css';

// Recipe Card Component
const RecipeCard = ({ recipe, currentUser, onComment, onCopy, onViewDetails }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentError, setCommentError] = useState('');
  const [posting, setPosting] = useState(false);
  const [copying, setCopying] = useState(false);
  useEffect(() => {
    if (!showComments) return undefined;
    setCommentError('');
    return onSnapshot(query(collection(db, 'publicRecipes', recipe.id, 'comments'), orderBy('createdAt', 'asc'), limit(100)),
      (snapshot) => setComments(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      () => setCommentError('Comments could not be loaded. Please try again.'));
  }, [showComments, recipe.id]);
  const displayedComments = [...(recipe.comments || []), ...comments];

  const handleSubmitComment = async () => {
    if (!commentText.trim() || posting) return;
    setPosting(true);
    try { if (await onComment(recipe.id, commentText)) setCommentText(''); }
    finally { setPosting(false); }
  };

  // Calculate soap properties display
  const oils = normalizeRecipe(recipe).items;
  const totalPct = oils.reduce((sum, o) => sum + (o.pct || 0), 0);

  return (
    <div className="recipe-card">
      <div className="recipe-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="recipe-info">
          <h3 className="recipe-name">{recipe.name}</h3>
          <div className="recipe-meta">
            <span className="recipe-author">by {recipe.authorName || 'Anonymous'}</span>
            <span className="recipe-date">
              {recipe.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
            </span>
          </div>
        </div>
        <div className="recipe-stats">
          <span className="stat">{recipe.commentCount || 0}</span>
          <span className="stat">{recipe.copyCount || 0} copies</span>
        </div>
      </div>

      {/* Quick Info */}
      <div className="recipe-quick-info">
        <span className="tag use-tag">{(recipe.use || ['body'])[0]}</span>
        <span className="tag goal-tag">{recipe.goal || 'balanced'}</span>
        <span className="tag sf-tag">SF {recipe.superfat ?? 5}%</span>
        <span className="tag oil-count">{oils.length} oils</span>
      </div>

      {expanded && (
        <div className="recipe-details">
          {/* Oil Blend */}
          <div className="oils-section">
            <h4>Oil Blend</h4>
            <div className="oils-list">
              {oils.map((oil, i) => (
                <div key={i} className="oil-item">
                  <span className="oil-name">{oil.name}</span>
                  <span className="oil-pct">{oil.pct}%</span>
                </div>
              ))}
            </div>
            {totalPct !== 100 && (
              <div className="pct-warning">Total: {totalPct}%</div>
            )}
          </div>

          {/* Additional Info */}
          {recipe.notes && (
            <div className="notes-section">
              <h4>Notes</h4>
              <p>{recipe.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="recipe-actions">
            <button className="action-btn copy-btn" disabled={copying} onClick={async () => { setCopying(true); try { await onCopy(recipe); } finally { setCopying(false); } }}>
              📋 Copy to My Recipes
            </button>
            <button 
              className="action-btn comments-btn"
              onClick={() => setShowComments(!showComments)}
            >
              {showComments ? 'Hide' : 'Show'} Comments ({recipe.commentCount || 0})
            </button>
          </div>

          {/* Comments Section */}
          {showComments && (
            <div className="comments-section">
              <h4>Comments</h4>
              
              {/* Comment Input */}
              <div className="comment-input">
                <textarea
                  aria-label="Recipe comment"
                  maxLength={5000}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  rows={2}
                />
                <button disabled={posting || !currentUser || !commentText.trim()} onClick={handleSubmitComment}>{posting ? 'Posting…' : 'Post Comment'}</button>
                {!currentUser && <p>Log in to post a comment.</p>}
              </div>

              {/* Comments List */}
              <div className="comments-list">
                {commentError && <p role="alert">{commentError}</p>}
                {displayedComments.length === 0 ? (
                  <p className="no-comments">No comments yet. Be the first!</p>
                ) : (
                  displayedComments.map((comment, i) => (
                    <div key={i} className="comment-item">
                      <div className="comment-header">
                        <span className="comment-author">{comment.authorName}</span>
                        <span className="comment-date">
                          {comment.createdAt?.toDate?.()?.toLocaleDateString() || 'Just now'}
                        </span>
                      </div>
                      <p className="comment-text">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Public Recipes Page
export default function PublicRecipesPage() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUse, setFilterUse] = useState('all');
  const [filterGoal, setFilterGoal] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [message, setMessage] = useState(null);
  const [signInPrompt, setSignInPrompt] = useState('');

  // Load public recipes
  useEffect(() => {
    const q = query(
      collection(db, 'publicRecipes'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recipesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecipes(recipesData);
      setLoading(false);
    }, (error) => {
      console.error('Public recipes load failed:', error);
      setLoading(false);
      setMessage({ type: 'error', text: 'Shared recipes could not be loaded. Please try again shortly.' });
    });

    return () => unsubscribe();
  }, []);

  // Add comment to recipe
  const handleComment = async (recipeId, commentText) => {
    if (!user) {
      setSignInPrompt('comment on recipes');
      return;
    }

    try {
      await addDoc(collection(db, 'publicRecipes', recipeId, 'comments'), {
        content: commentText.trim(),
        authorId: user.uid,
        authorName: user.displayName || 'Soap maker',
        createdAt: serverTimestamp(),
      });
      setMessage({ type: 'success', text: 'Comment added!' });
      return true;
    } catch (error) {
      console.error('Error adding comment:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to add comment' });
      return false;
    }
  };

  const handleCopyRecipe = async (recipe) => {
    if (!user) { setSignInPrompt('copy recipes to your collection'); return; }
    try {
      await httpsCallable(functions, 'copyPublicRecipe')({ recipeId: recipe.id });
      setMessage({ type: 'success', text: 'Recipe copied to your collection!' });
    } catch (error) {
      console.error('Error copying recipe:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to copy recipe' });
    }
  };

  // Filter and sort recipes
  const filteredRecipes = recipes
    .filter(recipe => {
      const matchesSearch = recipe.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesUse = filterUse === 'all' || recipe.use?.includes(filterUse);
      const matchesGoal = filterGoal === 'all' || recipe.goal === filterGoal;
      return matchesSearch && matchesUse && matchesGoal;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
      }
      if (sortBy === 'popular') {
        return (b.copyCount || 0) - (a.copyCount || 0);
      }
      if (sortBy === 'comments') {
        return (b.commentCount || 0) - (a.commentCount || 0);
      }
      return 0;
    });

  return (
    <>
      <div className="sl-page recipe-collection-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Recipes', to: '/recipes' },
            { label: 'Recipe Collection' },
          ]} />
          <div className="pageheading">Recipe Collection</div>
          <div className="pagedescription">Browse and discover recipes shared by the community.</div>
        </div>

        <div className="recipe-collection-content borderleft">
          <section className="mypadding"><SafetyNotice variant="community" /></section>
          {message && (
            <section className="mypadding">
              <div className={`message ${message.type}`}>
                {message.text}
                <button onClick={() => setMessage(null)}>×</button>
              </div>
            </section>
          )}

          {/* Filters */}
          <section className="mypadding borderbottom">
            <div className="rcp-filters">
              <input
                type="text"
                placeholder="Search recipes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select value={filterUse} onChange={(e) => setFilterUse(e.target.value)}>
                <option value="all">All Uses</option>
                <option value="face">Face</option>
                <option value="body">Body</option>
              </select>
              <select value={filterGoal} onChange={(e) => setFilterGoal(e.target.value)}>
                <option value="all">All Goals</option>
                <option value="balanced">Balanced</option>
                <option value="moisturising">Moisturizing</option>
                <option value="leather rich">Lather Rich</option>
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">Newest</option>
                <option value="popular">Most Copied</option>
                <option value="comments">Most Comments</option>
              </select>
            </div>
          </section>

          {/* Stats */}
          <section className="mypadding borderbottom">
            <div className="rcp-stats">
              <span>Showing <strong>{filteredRecipes.length}</strong> of <strong>{recipes.length}</strong> recipes</span>
            </div>
          </section>

          {/* Recipes Grid */}
          <section className="mypadding">
            {loading ? (
              <div className="rcp-empty">
                <p>Loading recipes...</p>
              </div>
            ) : filteredRecipes.length === 0 ? (
              <div className="rcp-empty">
                <p>No recipes found. Try adjusting your filters.</p>
              </div>
            ) : (
              <div className="recipes-grid">
                {filteredRecipes.map(recipe => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    currentUser={user}
                    onComment={handleComment}
                    onCopy={handleCopyRecipe}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      <SignInPrompt intent={signInPrompt} onClose={() => setSignInPrompt('')} />
      <Footer />
      </div>
    </>
  );
}
